<template>
  <!-- prettier-ignore -->
  <modal
    v-model="modalActive"
    locked
  >
    <div class="auth">
      <div
        class="auth__title"
        v-text="$t('screen.auth.title')"
      />

      <template v-if="!requested">
        <div
          class="auth__description"
          v-text="$t('screen.auth.request.description')"
        />

        <div
          class="auth__sentTAN"
          v-text="$t('screen.auth.recipient', { phone })"
        />

        <Form.Button
          class="auth__button"
          :disabled="smsDisabled"
          primary
          @click="sendTAN"
        >
          {{ $t('screen.auth.request.requestTAN') }}
        </Form.Button>
      </template>

      <template v-else-if="requested">
        <div
          class="auth__description"
          v-text="$t('screen.auth.confirm.description')"
        />

        <Form.Form
          autocomplete="off"
          class="auth-form"
          name="auth"
          tracking-disabled
          :validator="v"
          @submit="submit"
        >
          <Form.CodeInput
            v-model="formData.tan"
            autofocus
            numeric
            class="auth__input"
            mask="####"
            name="tan"
            theme="dark"
            :delay="0"
            :title="$t('screen.reentry.insertTan')"
            :validate="v.tan"
          />

          <Form.Button
            primary
            type="submit">
            {{ $t('form.next') }}
          </Form.Button>

          <Form.Button
            class="auth__button"
            tiny
            :disabled="smsDisabled"
            plain
            @click="sendTAN">
            {{ $t('screen.auth.confirm.requestTAN') }}
          </Form.Button>

        </Form.Form>

      </template>

      <a
        class="auth__end"
        @click="endSession"
        v-text="$t('screen.auth.end')"
      />
    </div>
  </modal>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, computed } from 'vue'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { loading$, events$ } from '@/services'

import Modal from '@/components/Modal/OvpModal'
import * as Form from '@/components/Form'

import { ROUTE_NAME, SESSION_TIME_TO_AUTH, SESSION_TIME_TO_CLEAR, STORAGE_KEY } from '@/config/constants'
import { EVENT_OVP, EVENT_SESSION } from '@/config/events'

import { useBasket, useNotifications, useSession, useStatus, useSync } from '@/hooks'

const { isOnboardingSecured } = useStatus()

// Hooks
const { basket, sendSMSTan, fetchBasket, persistBasket } = useBasket()
const { addErrorNotification, addNotification } = useNotifications()
const { clearSession, setSession } = useSession()
const { registerSync, unregisterSync } = useSync()
const { t } = useI18n()
const router = useRouter()

// Data
const modalActive = ref(false)
const requested = ref(false)
const formData = reactive({ tan: undefined })
const smsDisabled = ref(false)
const timerToAuth = ref(false)
const timerToClear = ref(false)
const v = useVuelidate(
  {
    tan: {
      required,
    },
  },
  formData,
  { $lazy: true }
)

// Lifecycle hook
onMounted(() => {
  // delay, leave time for the computeds to process
  window.setTimeout(() => on(), 5000)
  events$.on(EVENT_OVP.APPLICATION_SUBMITTED, () => {
    stopTimer()
    off()
  })
})

onBeforeUnmount(() => {
  off()
})

// Computed
const phone = computed(() => {
  if (!basket.mobile) return

  // TODO get mobile
  const start = basket.mobile.substr(0, 5)
  const inbetween = basket.mobile.substring(5, basket.mobile.length - 2).replace(/\d/g, '*')
  const end = basket.mobile.substr(-2)

  return `${start}${inbetween}${end}`
})

// Methods
async function endSession() {
  await clearSession()

  window.location.reload()
}

function off() {
  events$.off(EVENT_SESSION.UPDATE, reset)
}

function on() {
  events$.on(EVENT_SESSION.UPDATE, reset)
}

function reset() {
  if (basket.submitted) {
    stopTimer()
    off()
  }

  // if onboarding-secured and basket not yet submitted, register notification
  if (isOnboardingSecured.value && !basket.submitted) {
    stopTimer()

    timerToAuth.value = window.setTimeout(showAuthForm, SESSION_TIME_TO_AUTH)
  }
}

async function sendTAN() {
  loading$.start()
  smsDisabled.value = true
  requested.value = true

  stopTimer()

  await sendSMSTan(basket.reentryToken)
  loading$.end()
  addNotification(t('screen.auth.sent'))

  window.setTimeout(() => (smsDisabled.value = false), 60 * 2 * 1000)
}

/**
 * show auth-form modal
 *
 * turn off listener
 * start timer for session-ender
 *
 */
function showAuthForm() {
  // cancel events, as long as form is shown
  off()
  unregisterSync()
  window.localStorage.removeItem(STORAGE_KEY)
  timerToClear.value = window.setTimeout(() => endSession(), SESSION_TIME_TO_CLEAR)
  modalActive.value = true
}

function stopTimer() {
  // TODO fix type
  if (timerToAuth.value) window.clearTimeout(timerToAuth.value)
  if (timerToClear.value) window.clearTimeout(timerToClear.value)
}

/**
 * submit tan
 *
 * when response is submitted => redirect to nextSteps
 * when response is !submitted => turn on listener
 */
async function submit() {
  // clear timeouts
  stopTimer()

  const payload = {
    basketId: basket.basketId,
    tan: formData.tan,
    token: basket.reentryToken,
  }

  try {
    let __basket = await fetchBasket(payload)
    await setSession(__basket)
    await persistBasket(__basket)

    formData.tan = null
    requested.value = false
    v.value.$reset()

    if (basket.submitted) {
      router.push({ name: ROUTE_NAME.NEXT_STEPS })
    } else {
      on()
      reset()
    }

    // Needed because there is no reload to reactivate the sync
    registerSync()
    modalActive.value = false
  } catch (error) {
    addErrorNotification(error.infoMessages[0])
  }
}
</script>

<style name="mobile" scoped>
.auth {
  color: var(--c-black);
  font-size: 15px;
  line-height: 20px;
  padding: 20px;

  &__title {
    font-size: 20px;
    line-height: 25px;
    font-weight: bold;
  }

  &__description {
    margin: 20px 0 0 0;
  }

  &__sentTAN {
    margin: 20px 0;
  }

  &__input {
    margin: 20px 0;
  }

  &__button {
    color: var(--c-original-green);
    display: block;
  }

  button {
    margin: 0 0 20px;
    min-width: 200px;
  }

  &__end {
    display: block;
  }
}
</style>
